import { GraphQLResult } from '@aws-amplify/api-graphql';
import * as Sentry from '@sentry/react';
import logging from '@common/logging';
import { config as api } from '@services/api/config';
import appsync from '@services/appsync';
import * as mutations from '@services/appsync/mutations';
import * as queries from '@services/appsync/queries';
import * as types from '@services/appsync/types';
import contentful from '@services/contentful';
import { PortfolioAsset as PortfolioAssetRaw } from '@services/contentful/types';
import { mapField } from '@services/contentful/util';
import { Entry } from 'contentful';
import { PortfolioAsset } from 'src/apps/portfolio/types';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';

const client = generateClient();

const selectAuthMode = async () => {
  let user;
  // Choose auth mode based on whether the user is authenticated.
  try {
    user = await getCurrentUser();
  } catch (err) {
    if (err?.name !== 'UserUnAuthenticatedException') {
      Sentry.captureException(err);
    }
    return 'iam';
  }
  return !user ? 'iam' : 'userPool';
};

async function accreditSelf(variables: types.AccreditSelfMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.accreditSelf,
      variables: variables,
    })) as GraphQLResult<types.AccreditSelfMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function addBankAccount(variables: types.AddBankAccountViaPlaidMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.addBankAccountViaPlaid,
      variables: variables,
    })) as GraphQLResult<types.AddBankAccountViaPlaidMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    throw err;
  }
}

async function allDividendsV3(variables: types.AllDividendsV3QueryVariables): Promise<types.DividendsV3 | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allDividendsV3,
      variables: variables,
    })) as GraphQLResult<types.AllDividendsV3Query>;
    return result.data?.allDividendsV3 ?? null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function allMyInvestorRoles(variables?: types.AllMyUserInvestorRolesQueryVariables) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allMyUserInvestorRoles,
      variables: variables,
    })) as GraphQLResult<types.AllMyUserInvestorRolesQuery>;
    return result.data?.allMyUserInvestorRoles.items ?? [];
  } catch (err: any) {
    logging.logError(err);
    return [];
  }
}

async function allRewards(variables?: types.AllRewardsQueryVariables) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allRewards,
      variables: variables,
    })) as GraphQLResult<types.AllRewardsQuery>;
    return result.data?.allRewards.items ?? [];
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function allTrades(variables?: types.AllTradesQueryVariables) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allTrades,
      variables: variables,
    })) as GraphQLResult<types.AllTradesQuery>;
    return result.data?.allTrades.items ?? [];
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function allTransactionsV2(variables?: types.AllTransactionsV2QueryVariables) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allTransactionsV2,
      variables: variables,
    })) as GraphQLResult<types.AllTransactionsV2Query>;
    return result.data?.allTransactionsV2?.items ?? [];
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function approveInvestorAccountApplication(
  variables: types.ApproveInvestorAccountApplicationV2MutationVariables,
) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.approveInvestorAccountApplicationV2,
      variables: variables,
    })) as GraphQLResult<types.ApproveInvestorAccountApplicationV2Mutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function confirmPhoneNumberUpdate(variables: types.ConfirmPhoneNumberUpdateMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.confirmPhoneNumberUpdate,
      variables: variables,
    })) as GraphQLResult<types.ConfirmPhoneNumberUpdateMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function createBDAccount(variables: types.CreateBdAccountMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.createBdAccount,
      variables: variables,
    })) as GraphQLResult<types.CreateBdAccountMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function createOrUpdateInvestorAccountApplication(
  variables: types.CreateOrUpdateInvestorAccountApplicationMutationVariables,
) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.createOrUpdateInvestorAccountApplication,
      variables: variables,
    })) as GraphQLResult<types.CreateOrUpdateInvestorAccountApplicationMutation>;
    return result.data?.createOrUpdateInvestorAccountApplication.id || null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function createPlaidToken(variables: types.CreatePlaidLinkTokenMutationVariables): Promise<string> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.createPlaidLinkToken,
      variables: variables,
    })) as GraphQLResult<types.CreatePlaidLinkTokenMutation>;
    return result?.data?.createPlaidLinkToken ?? '';
  } catch (err: any) {
    logging.logError(err);
    return '';
  }
}

async function deleteBankAccount(variables: types.RemoveBankAccountMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.removeBankAccount,
      variables: variables,
    })) as GraphQLResult<types.RemoveBankAccountMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function deleteUnconfirmedUser(variables: types.DeleteUnconfirmedUserMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.deleteUnconfirmedUser,
      variables: variables,
    })) as GraphQLResult<types.DeleteUnconfirmedUserMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function getAccountValue(variables: types.GetAccountValueV2QueryVariables): Promise<types.PositionValue | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getAccountValueV2,
      variables: variables,
    })) as GraphQLResult<types.GetAccountValueV2Query>;
    return result.data?.getAccountValueV2 ?? null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getBankAccounts(variables: types.GetBankAccountsQueryVariables): Promise<types.BankDetail[]> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getBankAccounts,
      variables: variables,
    })) as GraphQLResult<types.GetBankAccountsQuery>;
    return (result?.data?.getBankAccounts as types.BankDetail[]) || [];
  } catch (err: any) {
    logging.logError(err);
    return [];
  }
}

async function getInvestPreflightInfo(
  variables: types.GetInvestPreflightInfoQueryVariables,
): Promise<types.InvestPreflightInfo | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getInvestPreflightInfo,
      variables: variables,
    })) as GraphQLResult<types.GetInvestPreflightInfoQuery>;
    return result?.data?.getInvestPreflightInfo || null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getInvestorAccount(
  variables: types.GetInvestorAccountQueryVariables,
): Promise<types.InvestorAccount | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getInvestorAccount,
      variables: variables,
    })) as GraphQLResult<types.GetInvestorAccountQuery>;
    return result?.data?.getInvestorAccount || null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getMyProfile(variables?: types.GetMyProfileQueryVariables) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getMyProfile,
      variables: variables,
    })) as GraphQLResult<types.GetMyProfileQuery>;
    return result;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getPortfolioAsset(slug: string, preview = false) {
  const client = preview ? contentful.previewClient : contentful.client;
  const request = await client.getEntries({
    content_type: 'portfolioAsset',
    'fields.slug': slug,
    include: 10,
  });

  if (request.items.length === 0) return null;

  const item = request.items[0] as unknown as Entry<PortfolioAssetRaw>;

  const asset: PortfolioAsset = {
    assetHighlights: item.fields.highlights.fields,
    header: {
      headerImage: item.fields.image.fields,
      locationName: item.fields.locationName,
      title: item.fields.name,
    },
    images: [],
  };

  Object.keys(item.fields).map((key) => {
    mapField(key as keyof PortfolioAssetRaw, asset, item);
  });
  return asset;
}

async function getSubscription(variables: types.GetSubscriptionQueryVariables): Promise<types.Subscription | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getSubscription,
      variables: variables,
    })) as GraphQLResult<types.GetSubscriptionQuery>;
    return result.data?.getSubscription ?? null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getSubscriptionOffer(
  variables: types.GetSubscriptionOfferV2QueryVariables,
): Promise<types.SubscriptionOffer | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getSubscriptionOfferV2,
      variables: variables,
    })) as GraphQLResult<types.GetSubscriptionOfferV2Query>;
    return result.data?.getSubscriptionOfferV2 ?? null;
  } catch (err: any) {
    if (err.data?.getSubscriptionOfferV2) {
      // If the query returned a result, then return it and ignore errors.
      // Errors are likely the result of per-field permissions errors.
      // TODO: This is a hacky way to handle this. We should have a better way.
      return err.data?.getSubscriptionOfferV2;
    } else {
      logging.logError(err);
      return null;
    }
  }
}

async function invest(variables: types.InvestMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.invest,
      variables: variables,
    })) as GraphQLResult<types.InvestMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function isEmailAvailable(variables: types.IsEmailAvailableQueryVariables): Promise<boolean> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.isEmailAvailable,
      variables: variables,
    })) as GraphQLResult<types.IsEmailAvailableQuery>;
    return result?.data?.isEmailAvailable ?? false;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function listSubscriptionOffers(
  variables: types.ListSubscriptionOffersQueryVariables,
): Promise<types.SubscriptionOffer[]> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.listSubscriptionOffers,
      variables: variables,
    })) as GraphQLResult<types.ListSubscriptionOffersQuery>;
    return result.data?.listSubscriptionOffers.items ?? [];
  } catch (err: any) {
    if (err.data?.listSubscriptionOffers) {
      // If the query returned a result, then return it and ignore errors.
      // Errors are likely the result of per-field permissions errors.
      // TODO: This is a hacky way to handle this. We should have a better way.
      return err.data?.listSubscriptionOffers.items ?? [];
    } else {
      logging.logError(err);
      return [];
    }
  }
}

async function listSubscriptions(variables: types.ListSubscriptionsQueryVariables): Promise<types.Subscription[]> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.listSubscriptions,
      variables: variables,
    })) as GraphQLResult<types.ListSubscriptionsQuery>;
    return result.data?.listSubscriptions ?? [];
  } catch (err: any) {
    logging.logError(err);
    return [];
  }
}

async function setDefaultBankAccount(variables: types.SetDefaultBankAccountMutationVariables) {
  try {
    const response = (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.setDefaultBankAccount,
      variables: variables,
    })) as GraphQLResult<types.SetDefaultBankAccountMutation>;
    return response.data?.setDefaultBankAccount;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function setPricingPlan(variables: types.SetPricingPlanMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.setPricingPlan,
      variables: variables,
    })) as GraphQLResult<types.SetPricingPlanMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function storeHeadshot(variables: types.StoreHeadshotMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.storeHeadshot,
      variables: variables,
    })) as GraphQLResult<types.StoreHeadshotMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function storeSignatureFile(variables: types.StoreSignatureFileMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.storeSignatureFile,
      variables: variables,
    })) as GraphQLResult<types.StoreSignatureFileMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function updateInvestorAccount(variables: types.UpdateInvestorAccountMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.updateInvestorAccount,
      variables: variables,
    })) as GraphQLResult<types.UpdateInvestorAccountMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function updateProfile(variables: types.UpdateProfileMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.updateProfile,
      variables: variables,
    })) as GraphQLResult<types.UpdateProfileMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

// XXX: Do not update employment information through this function
// it will result in a broken account due to a "null" value being assigned to the employment information
async function updateSuitabilityV2(variables: types.UpdateSuitabilityV2MutationVariables) {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.updateSuitabilityV2,
      variables: variables,
    })) as GraphQLResult<types.UpdateSuitabilityV2Mutation>;
    return result.data?.updateSuitabilityV2;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function verifyEmail(token = ''): Promise<{ success: boolean }> {
  if (!api.endpoint) return Promise.reject({ success: false });
  const endpoint = `${api.endpoint}/public/verify-email`;
  return fetch(endpoint, {
    body: JSON.stringify({ token }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
    .then((response) => {
      if (!response.ok) throw new Error('Email verification failed');
      return { success: true };
    })
    .catch((err) => {
      logging.logError(err, { endpoint });
      return { success: false };
    });
}

async function resendEmailVerification(variables: types.ResendEmailVerificationMutationVariables) {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.resendEmailVerification,
      variables: variables,
    })) as GraphQLResult<types.ResendEmailVerificationMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}
async function acceptTermsAndConditions(variables: types.AcceptTermsAndConditionsV2MutationVariables) {
  try {
    await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.acceptTermsAndConditionsV2,
      variables: variables,
    });
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}
async function finishPlaidBankAccountVerify(variables: types.FinishPlaidBankAccountVerifyMutationVariables) {
  try {
    await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.finishPlaidBankAccountVerify,
      variables: variables,
    });
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}
async function updateSubscriptionOffer(variables: types.UpdateSubscriptionOfferMutationVariables) {
  try {
    await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.updateSubscriptionOffer,
      variables: variables,
    });
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}
async function createAutoInvestScheduleV2(variables: types.CreateAutoInvestScheduleV2MutationVariables) {
  try {
    await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.createAutoInvestScheduleV2,
      variables: variables,
    });
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}
async function deleteAutoInvestSchedule(variables: types.DeleteAutoInvestScheduleV2MutationVariables) {
  try {
    await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.deleteAutoInvestScheduleV2,
      variables: variables,
    });
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}
async function modifyAutoInvestSchedule(variables: types.ModifyAutoInvestScheduleMutationVariables) {
  try {
    await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.modifyAutoInvestSchedule,
      variables: variables,
    });
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

async function getConsolidatedInvestor(
  variables: types.GetConsolidatedInvestorQueryVariables,
): Promise<types.ConsolidatedInvestor | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getConsolidatedInvestor,
      variables: variables,
    })) as GraphQLResult<types.GetConsolidatedInvestorQuery>;
    return result.data?.getConsolidatedInvestor ?? null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}
async function allAutoInvestSchedules(
  variables: types.AllAutoInvestSchedulesV2QueryVariables,
): Promise<types.AutoInvestScheduleV2[]> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allAutoInvestSchedulesV2,
      variables: variables,
    })) as GraphQLResult<types.AllAutoInvestSchedulesV2Query>;
    return result.data?.allAutoInvestSchedulesV2.items ?? [];
  } catch (err: any) {
    logging.logError(err);
    return [];
  }
}

async function getInvestorTaxDocuments(
  variables: types.GetInvestorTaxDocumentsQueryVariables,
): Promise<types.GetInvestorTaxDocumentsQuery['getInvestorTaxDocuments']> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getInvestorTaxDocuments,
      variables: variables,
    })) as GraphQLResult<types.GetInvestorTaxDocumentsQuery>;
    return result.data?.getInvestorTaxDocuments ?? [];
  } catch (err: any) {
    logging.logError(err);
    return [];
  }
}

async function getTaxDocumentPresignedURL(
  variables: types.GetTaxDocumentPresignedURLQueryVariables,
): Promise<types.GetTaxDocumentPresignedURLQuery['getTaxDocumentPresignedURL']['download_url']> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getTaxDocumentPresignedURL,
      variables: variables,
    })) as GraphQLResult<types.GetTaxDocumentPresignedURLQuery>;
    return result.data?.getTaxDocumentPresignedURL.download_url;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getMonthlyStatement(
  variables: types.GetMonthlyStatementQueryVariables,
): Promise<types.MonthlyStatement | null> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.getMonthlyStatement,
      variables: variables,
    })) as GraphQLResult<types.GetMonthlyStatementQuery>;
    return result.data?.getMonthlyStatement ?? null;
  } catch (err: any) {
    logging.logError(err);
    return null;
  }
}

async function getPartners(): Promise<types.Partner[]> {
  try {
    const result = (await client.graphql({
      authMode: await selectAuthMode(),
      query: queries.allPartners,
    })) as GraphQLResult<types.AllPartnersQuery>;
    return result.data?.allPartners ?? [];
  } catch (err: any) {
    logging.logError(err);
    return [];
  }
}

async function savePartner(variables: types.UpsertPartnerMutationVariables): Promise<boolean> {
  try {
    (await client.graphql({
      authMode: await selectAuthMode(),
      query: mutations.upsertPartner,
      variables: variables,
    })) as GraphQLResult<types.UpsertPartnerMutation>;
    return true;
  } catch (err: any) {
    logging.logError(err);
    return false;
  }
}

let hasInitialized = false;
export default (() => {
  if (!hasInitialized) {
    appsync; // (default export is an iife that calls the necessary initialization methods required to use appsync downstream)
    hasInitialized = true;
  }
  return {
    acceptTermsAndConditions,
    accreditSelf,
    addBankAccount,
    allAutoInvestSchedules,
    allDividendsV3,
    allMyInvestorRoles,
    allRewards,
    allTrades,
    allTransactionsV2,
    approveInvestorAccountApplication,
    confirmPhoneNumberUpdate,
    createAutoInvestScheduleV2,
    createBDAccount,
    createOrUpdateInvestorAccountApplication,
    createPlaidToken,
    deleteAutoInvestSchedule,
    deleteBankAccount,
    deleteUnconfirmedUser,
    finishPlaidBankAccountVerify,
    getAccountValue,
    getBankAccounts,
    getConsolidatedInvestor,
    getInvestPreflightInfo,
    getInvestorAccount,
    getInvestorTaxDocuments,
    getMonthlyStatement,
    getMyProfile,
    getPartners,
    getPortfolioAsset,
    getSubscription,
    getSubscriptionOffer,
    getTaxDocumentPresignedURL,
    invest,
    isEmailAvailable,
    listSubscriptionOffers,
    listSubscriptions,
    modifyAutoInvestSchedule,
    resendEmailVerification,
    savePartner,
    setDefaultBankAccount,
    setPricingPlan,
    storeHeadshot,
    storeSignatureFile,
    updateInvestorAccount,
    updateProfile,
    updateSubscriptionOffer,
    updateSuitabilityV2,
    verifyEmail,
  };
})();
